import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import {
  Button,
  Checkbox,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import Info from '@material-ui/icons/Info';
import Warning from '@material-ui/icons/Warning';
import { formatMoney } from '../../helpers/invoiceHelpers';
import { formatPercent, formatRates } from './refundFormat';
import MoneyField from './MoneyField';
import { keys } from './constants';

// One selectable row per fiscal line. A multi-tax line (coupled taxes on one base) is a
// single row with several rate chips and one amount input. The total budget and per-rate
// caps are the constraints (enforced by the dialog); the original price is a guideline.
const styles = ({ spacing: { unit }, palette }) => ({
  clearRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: unit,
  },
  tableWrapper: {
    width: '100%',
    overflowX: 'auto',
  },
  table: {
    minWidth: 540,
  },
  head: {
    color: palette.text.secondary,
    fontWeight: 600,
    fontSize: '0.7rem',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  right: {
    textAlign: 'right',
  },
  numeric: {
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
  },
  rateChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: `${unit / 2}px`,
  },
  rateChip: {
    height: unit * 2.5,
    border: `1px solid ${palette.divider}`,
    backgroundColor: 'transparent',
  },
  originalHeader: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: `${unit / 2}px`,
    cursor: 'help',
  },
  infoIcon: {
    fontSize: 15,
    color: palette.text.disabled,
  },
  amountCell: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: `${unit / 2}px`,
  },
  warnIcon: {
    fontSize: 18,
    color: palette.secondary ? palette.secondary.main : '#ed6c02',
  },
  help: {
    display: 'block',
    marginTop: unit,
  },
});

const RefundLinesTable = ({
  classes,
  translate,
  lines,
  locale,
  currency,
  currencySymbol,
  decimals,
  disabled,
  isOverBudget,
  overRefs,
  selectedCount,
  onToggleLine,
  onChangeAmount,
  onClearAll,
}) => {
  const money = value => formatMoney(value, locale, currency);

  return (
    <>
      <div className={classes.clearRow}>
        <Button size="small" onClick={onClearAll} disabled={disabled || selectedCount === 0}>
          {translate(keys.clearAll)}
        </Button>
      </div>
      <div className={classes.tableWrapper}>
        <Table className={classes.table} padding="dense">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell className={classes.head}>{translate(keys.colItem)}</TableCell>
              <TableCell className={classes.head}>{translate(keys.colVat)}</TableCell>
              <TableCell className={`${classes.head} ${classes.right}`}>
                <Tooltip title={translate(keys.colOriginalInfo)}>
                  <span className={classes.originalHeader}>
                    {translate(keys.colOriginal)}
                    <Info className={classes.infoIcon} />
                  </span>
                </Tooltip>
              </TableCell>
              <TableCell className={`${classes.head} ${classes.right}`}>{translate(keys.colRefund)}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((line, idx) => {
              const labelId = `refline-${idx}`;
              const overRate = overRefs && overRefs.has(line.ref);
              const showAmountError = (isOverBudget || overRate) && line.selected;
              return (
                <TableRow key={line.ref} hover selected={line.isActive}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      id={`Selenium-RefundByItem-checkbox-${line.ref}`}
                      checked={line.isActive}
                      disabled={disabled}
                      onChange={() => onToggleLine(line.ref)}
                      inputProps={{ 'aria-labelledby': labelId }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography id={labelId} variant="body2">
                      {line.label}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <div className={classes.rateChips}>
                      {line.rates.map(rate => (
                        <Chip key={rate} label={formatPercent(rate)} className={classes.rateChip} />
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className={classes.numeric}>{money(line.originalPrice)}</TableCell>
                  <TableCell className={classes.right}>
                    {line.isActive ? (
                      <div className={classes.amountCell}>
                        {line.isOverOriginal && !showAmountError && (
                          <Tooltip
                            title={translate(keys.overOriginal, {
                              amount: money(line.amount),
                              original: money(line.originalPrice),
                            })}
                          >
                            <Warning className={classes.warnIcon} />
                          </Tooltip>
                        )}
                        <MoneyField
                          value={line.rawAmount}
                          currencySymbol={currencySymbol}
                          decimals={decimals}
                          disabled={disabled}
                          error={showAmountError}
                          ariaLabel={translate(keys.amountAria, { line: `${line.name} ${formatRates(line.rates)}` })}
                          onChange={next => onChangeAmount(line.ref, next)}
                        />
                      </div>
                    ) : (
                      <Typography component="span" color="textSecondary">
                        &mdash;
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <Typography variant="caption" color="textSecondary" className={classes.help}>
        {translate(keys.tableHelp)}
      </Typography>
    </>
  );
};

RefundLinesTable.propTypes = {
  classes: PropTypes.shape({}),
  translate: PropTypes.func.isRequired,
  lines: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  locale: PropTypes.string,
  currency: PropTypes.string,
  currencySymbol: PropTypes.string,
  decimals: PropTypes.number,
  disabled: PropTypes.bool,
  isOverBudget: PropTypes.bool,
  overRefs: PropTypes.instanceOf(Set),
  selectedCount: PropTypes.number,
  onToggleLine: PropTypes.func.isRequired,
  onChangeAmount: PropTypes.func.isRequired,
  onClearAll: PropTypes.func.isRequired,
};

export const RefundLinesTableTest = RefundLinesTable;

export default withStyles(styles)(RefundLinesTable);
